/**
 * The narrative approach to the exhibition index.
 *
 * The grid answers "which one?" but says nothing about why any of them is worth
 * walking through. This puts one full-bleed chapter in front of each exhibition,
 * revealed as it is scrolled into view, and leaves the grid at the end for anyone
 * who already knows where they are going.
 *
 * Three motion systems, kept separate on purpose:
 *
 *   Reveal   IntersectionObserver, one-shot per element. Cheap, and it never runs
 *            again once a chapter has been seen.
 *   Parallax Scroll-linked, rAF-throttled, writing a single custom property. The
 *            image drifts slower than the text, which is what gives depth.
 *   Progress How far the chapter has travelled through its own pinned run, 0 at the
 *            moment it fills the scrollport and 1 as it leaves. `--parallax` cannot
 *            do this job: it is centre-relative and symmetric, so it reads the same
 *            entering and leaving, while the slow push into the image has to be
 *            monotonic or it visibly rewinds halfway through.
 *
 * All three are disabled outright under prefers-reduced-motion: content arrives
 * already visible rather than animating faster.
 */

import { dustChurning, openingDust, washIntensity } from "./veil";
import { motionAllowed } from "./motion";

export type Story = { dispose: () => void };

const REVEAL_MARGIN = "0px 0px -12% 0px";
/** How much has to be showing before a section fades in. */
const REVEAL_RATIO = 0.15;
/**
 * The crossings the reveal observer is told to watch for.
 *
 * A ladder rather than the single step this used to be, because `threshold` measures a
 * fraction of the *element* and an element taller than the scrollport can never reach
 * one. Six rooms make the Saudi showcase about 4,900px against an 800px window, whose
 * largest possible ratio is 0.14 — under the bar, for ever. The section sat at opacity 0
 * down its whole height: several seconds of scrolling through black, with the rooms
 * loading correctly and invisibly behind it. Greece, one card shorter, cleared 0.15 by a
 * hair, which is why only one heritage looked broken.
 *
 * So the rule is "15% of the element, or 15% of the screen, whichever comes first", and
 * the lower rungs are what give the callback a chance to notice the second one.
 */
const REVEAL_STEPS = [0, 0.02, 0.05, 0.1, REVEAL_RATIO];

export function animateStory(root: HTMLElement): Story {
  const still = !motionAllowed();
  // Chapters and opening scenes both want the scroll signal, but only chapters are
  // exhibitions. Keeping one attribute for "is an exhibition" and another for "wants
  // --progress" stops the two questions from having to share an answer.
  const chapters = [...root.querySelectorAll<HTMLElement>("[data-chapter], [data-scene]")];
  const reveals = [...root.querySelectorAll<HTMLElement>("[data-reveal]")];

  if (still) {
    // Mark everything shown so the CSS start-state never applies.
    for (const element of reveals) element.dataset.shown = "true";
    return { dispose: () => undefined };
  }

  const observer = new IntersectionObserver(
    (entries) => {
      for (const entry of entries) {
        if (!entry.isIntersecting) continue;
        const root = entry.rootBounds;
        const enough =
          entry.intersectionRatio >= REVEAL_RATIO ||
          (root !== null && root.height > 0 && entry.intersectionRect.height >= root.height * REVEAL_RATIO);
        if (!enough) continue;
        const element = entry.target as HTMLElement;
        element.dataset.shown = "true";
        // One-shot: re-revealing on every pass back up the page is distracting.
        observer.unobserve(element);
      }
    },
    { rootMargin: REVEAL_MARGIN, threshold: REVEAL_STEPS },
  );
  for (const element of reveals) observer.observe(element);

  let frame = 0;

  const paint = (): void => {
    frame = 0;
    let density = 0;
    let wash = 0;
    let churning = false;
    // The index scrolls inside its own element, so the scrollport is that element and
    // not the window. Measuring against innerHeight instead works only while the two
    // happen to be the same size, which stops being true the moment the page is
    // embedded or the kiosk chrome takes a slice of the viewport.
    const view = root.getBoundingClientRect();
    for (const [index, chapter] of chapters.entries()) {
      const rect = chapter.getBoundingClientRect();
      if (rect.bottom < view.top || rect.top > view.bottom) continue;
      const offset = rect.top - view.top;
      // -1 when the chapter is entering from below, +1 when it has left above.
      const centred = (offset + rect.height / 2 - view.height / 2) / view.height;
      chapter.style.setProperty("--parallax", clamp(centred, -1, 1).toFixed(4));

      // A chapter taller than the scrollport pins for `run` pixels; one that is not
      // taller has no run at all, and dividing by that zero would write NaN into the
      // custom property and drop every declaration that reads it.
      const run = rect.height - view.height;
      const progress = run > 0 ? clamp(-offset / run, 0, 1) : 0;
      chapter.style.setProperty("--progress", progress.toFixed(4));

      // One shared veil, not one per scene. A veil inside a stage is clipped to that
      // stage, so at a transition two independently rotating dust fields meet along the
      // stage edge and the join is a hard horizontal line straight across the frame.
      // Taking the maximum across the scenes in view drives a single field over the
      // whole scrollport, which has no edge to show.
      const last = index === chapters.length - 1;
      // The dust is spent once, on the opening, and it rises and clears inside that
      // scene's own run. Every scene after it hands over through the haze.
      if (index === 0) {
        density = Math.max(density, openingDust(progress));
        if (dustChurning(progress)) churning = true;
      } else {
        wash = Math.max(wash, washIntensity(progress, false, last));
      }
    }

    root.style.setProperty("--veil", density.toFixed(4));
    root.style.setProperty("--wash", wash.toFixed(4));
    // Three full-viewport layers rotating permanently is real cost for something that
    // is now visible for one stretch of one scene.
    const veiling = churning ? "true" : "false";
    if (root.dataset.veiling !== veiling) root.dataset.veiling = veiling;
  };

  const onScroll = (): void => {
    if (frame === 0) frame = requestAnimationFrame(paint);
  };

  paint();
  // The index scrolls inside its own element, not the document.
  root.addEventListener("scroll", onScroll, { passive: true });
  window.addEventListener("resize", onScroll, { passive: true });

  return {
    dispose: () => {
      cancelAnimationFrame(frame);
      observer.disconnect();
      root.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
    },
  };
}

function clamp(value: number, low: number, high: number): number {
  return Math.min(high, Math.max(low, value));
}
